import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Hash,
  MessageSquareText,
  Building2,
  Flag,
  CalendarClock,
  Clock3,
  CheckCircle2,
  SendHorizonal,
  X,
} from "lucide-react";

import useTickets from "../../services/Support/useTickets";
import { PriorityBadge, StatusBadge } from "./TicketBadge";
import "./ClientTicket.css";
import NoIndex from "../../components/NoIndex";
import LoadingScreen from "../../utils/LoadingScreen";
import { formatDateTime } from "../../utils/dateFormatter";
import { ToastContainer } from "react-toastify";

const ClientTicketDrawer = ({ open, onClose, ticket }) => {
  const { fetchConversation, sendMessage } = useTickets();

  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [input, setInput] = useState("");
  const [status, setStatus] = useState(ticket?.status || "O");

  const listRef = useRef(null);

  const ticketId = ticket?.pk_ticket_id;

  useEffect(() => {
    setStatus(ticket?.status || "O");
  }, [ticket]);

  // LOAD CHAT

  const loadConversation = async () => {
    if (!ticketId) return;

    setMsgLoading(true);

    const res = await fetchConversation(ticketId);

    if (res?.responseCode === "00") {
      setMessages(res.response || []);
    } else {
      setMessages([]);
    }

    setMsgLoading(false);

    setTimeout(() => {
      listRef.current?.scrollTo({
        top: 999999,
        behavior: "smooth",
      });
    }, 50);
  };

  useEffect(() => {
    if (open && ticketId) {
      loadConversation();
    }
  }, [open, ticketId]);

  // SEND MESSAGE

  const send = async () => {
    const text = input.trim();

    if (!text) return;

    const res = await sendMessage({
      ticketid: ticketId,
      message: text,
    });

    if (res?.responseCode === "00") {
      setInput("");
      loadConversation();
    }
  };

  // META INFO

  const metaItems = useMemo(() => {
    if (!ticket) return [];

    return [
      { key: "Ticket Code", val: ticket.ticket_code || "-", icon: Hash },
      { key: "Subject", val: ticket.subject || "-", icon: MessageSquareText },
      { key: "Department", val: ticket.department || "-", icon: Building2 },
      {
        key: "Priority",
        val: <PriorityBadge value={ticket.priority} />,
        icon: Flag,
      },
     {
  key: "Created",
  val: formatDateTime(ticket.created_on, true),
  icon: CalendarClock,
},
{
  key: "Updated",
  val: formatDateTime(ticket.updated_on, true),
  icon: Clock3,
},
{
  key: "Closed On",
  val: formatDateTime(ticket.closed_on, true),
  icon: CheckCircle2,
},
    ];
  }, [ticket]);

  // CLIENT MESSAGE CHECK

  const isClientMsg = (m) => {
    if (m?.sender) {
      return m.sender.toLowerCase() === "client";
    }
    return !!m?.fk_user_id;
  };

  if (!open) return null; 
  
  return (
    <>
    
   <NoIndex/>
   <ToastContainer/>
    <div className="fixed inset-0 z-50 flex items-center justify-center montserrat-font-1">
  <div className="bg-white w-[600px] max-h-[90vh] rounded-lg shadow-xl overflow-y-auto montserrat-font-1">
      {/* HEADER */}

      <div className="flex justify-between items-center p-4 border-b montserrat-font-1">
        <div>
          <h3 className="font-semibold text-lg flex items-center gap-2">
            Ticket #{ticket?.ticket_code}
            <StatusBadge code={status} />
          </h3>

          <div className="text-sm text-gray-500">{ticket?.subject}</div>
        </div>

        <button
          onClick={() => onClose(false)}
          className="text-gray-500 hover:text-black"
        >
          <X size={20} />
        </button>
      </div>

      {/* BODY */}

      <div className="flex flex-col h-[calc(100%-70px)] p-4 montserrat-font-1">
        {/* META GRID */}

        <div className="grid grid-cols-2 gap-4 mb-4">
          {metaItems.map((m, i) => {
            const Icon = m.icon;

            const styles = [
              "bg-blue-50 border-blue-200 text-blue-700",
              "bg-purple-50 border-purple-200 text-purple-700",
              "bg-teal-50 border-teal-200 text-teal-700",
              "bg-orange-50 border-orange-200 text-orange-700",
              "bg-pink-50 border-pink-200 text-pink-700",
              "bg-indigo-50 border-indigo-200 text-indigo-700",
              "bg-green-50 border-green-200 text-green-700",
            ];

            const style = styles[i % styles.length];

            return (
              <div
                key={m.key}
                className={`flex items-center gap-3 p-4 rounded-xl montserrat-font-1 border ${style}`}
              >
                {/* ICON */}

                <div className="flex items-center justify-center w-10 h-10 rounded-lg border bg-white">
                  <Icon size={18} />
                </div>

                {/* CONTENT */}

                <div className="flex flex-col montserrat-font-1">
                  <span className="text-xs font-semibold uppercase opacity-70">
                    {m.key}
                  </span>

                  <span className="text-sm font-semibold">{m.val}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* CHAT */}

        <div className="flex flex-col flex-grow border rounded-lg overflow-hidden montserrat-font-1">
          {/* CHAT HEADER */}

          <div className="flex justify-between items-center p-3 border-b bg-gray-50">
            <div className="font-medium">Chat with Support</div>

            <button
              onClick={loadConversation}
              className="text-sm border px-3 py-1 rounded hover:bg-gray-100"
            >
              {msgLoading ? "Loading..." : "Refresh"}
            </button>
          </div>

          {/* MESSAGES */}

          <div
            ref={listRef}
            className="flex-grow p-3 overflow-y-auto space-y-3 montserrat-font-1"
          >
            {msgLoading ? (
              <div className="flex justify-center p-6">
                <LoadingScreen text="Updating....." />
              </div>
            ) : messages.length > 0 ? (
              messages.map((m, i) => {
                const clientSide = isClientMsg(m);

                return (
                  <div
                    key={i}
                    className={`flex ${
                      clientSide ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[70%] p-3 rounded-lg text-sm ${
                        clientSide
                          ? "bg-green-500 text-white"
                          : "bg-gray-200 text-gray-800"
                      }`}
                    >
                      <div>{m.message}</div>

                     <div className="text-xs opacity-70 mt-1">
  {formatDateTime(m.created_on, true)}
</div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center text-gray-500">No messages yet</div>
            )}
          </div>

          {/* INPUT */}

          <div className="border-t p-3 flex gap-2 montserrat-font-1">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Write a message..."
              onKeyDown={(e) => e.key === "Enter" && send()}
              className="flex-1 border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            />

            <button
              onClick={send}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              <SendHorizonal size={16} />
              Send
            </button>
          </div>
        </div>

        {/* CLOSE BUTTON */}

        <button
          onClick={() => onClose(false)}
          className="mt-4 border py-2 rounded hover:bg-gray-100"
        >
          Close
        </button>
      </div>
    </div>
    </div>
    </>
  );
};

export default ClientTicketDrawer;
